import React, { SetStateAction, useCallback, useState } from 'react';
import PhoneInput, {
  type Country,
  getCountryCallingCode,
  isValidPhoneNumber,
} from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

export interface IExternalUsers {
  isEnabled: { email: boolean; text: boolean };
  emails: string[];
  setEmails: React.Dispatch<SetStateAction<string[]>>;
  setIsEnabled: React.Dispatch<
    SetStateAction<{ email: boolean; text: boolean }>
  >;
  texts: string[];
  setTexts: React.Dispatch<SetStateAction<string[]>>;
  errors: { emailError: string; phoneError: string };
  setErrors: React.Dispatch<
    React.SetStateAction<{
      emailError: string;
      phoneError: string;
    }>
  >;
}
const ExternalUser: React.FC<IExternalUsers> = ({
  isEnabled,
  emails,
  setEmails,
  setIsEnabled,
  texts,
  setTexts,
  errors,
  setErrors,
}) => {
  const [countryCode, setCountryCode] = useState<string>('1');
  const [country, setCountry] = useState<Country>('US');
  const [emailVal, setEmailVal] = useState<string>('');
  const [textVal, setTextVal] = useState<string>('');

  const checkValidEmail = useCallback(() => {
    if (emailVal.length === 0) return false;
    const isValid = emailRegex.test(emailVal);
    if (isValid) {
      if (emails.includes(emailVal)) {
        setErrors({ ...errors, emailError: 'Email already added' });
        return false;
      }
      setErrors({ ...errors, emailError: '' });
    } else {
      setErrors({ ...errors, emailError: 'Enter valid email' });
      return false;
    }
    return true;
  }, [emailVal, setErrors, errors, emails]);

  const validateNumber = useCallback(() => {
    if (textVal.length === 0) {
      return false;
    }
    const phone = isValidPhoneNumber(textVal ?? '', countryCode as Country);
    if (phone) {
      if (texts.includes(textVal)) {
        setErrors({ ...errors, phoneError: 'Phone number already added' });
        return false;
      }
      setErrors({ ...errors, phoneError: '' });
      return true;
    } else {
      setErrors({ ...errors, phoneError: 'Enter valid number' });
      return false;
    }
  }, [countryCode, textVal, setErrors, errors, texts]);

  const addEmail = useCallback(() => {
    const isValid = checkValidEmail();
    if (isValid) {
      setEmails((prev) => [...prev, emailVal]);
      setEmailVal('');
    }
  }, [checkValidEmail, setEmails, emailVal]);

  const removeEmail = useCallback(
    (index: number) => {
      setEmails(emails.filter((_, i) => i !== index));
    },
    [setEmails, emails]
  );

  const removeNumber = useCallback(
    (index: number) => {
      setTexts(texts.filter((_, i) => i !== index));
    },
    [setTexts, texts]
  );

  const addPhone = useCallback(() => {
    if (textVal.trim().length === 0) {
      setErrors({
        ...errors,
        phoneError: 'Please enter phone number',
      });
      return;
    }
    const isValid = validateNumber();
    if (isValid) {
      setTexts((prev) => [...prev, textVal]);
      setTextVal('');
    }
  }, [textVal, validateNumber, setErrors, errors, setTexts]);

  return (
    <div>
      <div className="flex items-center gap-7">
        <label className="custom-container w-auto pl-8">
          <span className="text-gradient-orange text-[16px]">
            External Email
          </span>
          <input
            type="checkbox"
            name="checboxname"
            id="email check"
            checked={isEnabled.email}
            onChange={(e) => {
              setIsEnabled({ ...isEnabled, email: e.target.checked });
            }}
          />
          <span className="checkmark"></span>
        </label>
        <label className="custom-container w-auto pl-8">
          <span className="text-gradient-orange text-[16px] block">Text</span>
          <input
            type="checkbox"
            name="checboxname"
            id="text check"
            checked={isEnabled.text}
            onChange={(e) => {
              setIsEnabled({ ...isEnabled, text: e.target.checked });
            }}
          />
          <span className="checkmark"></span>
        </label>
      </div>
      <div className="my-5">
        {isEnabled.email && (
          <>
            <div className="flex items-center gap-2 mb-2">
              <input
                className="input-border !rounded-lg !p-4 !w-[calc(100%-30px)]"
                onBlur={checkValidEmail}
                disabled={!isEnabled.email}
                placeholder="example@example.com"
                value={emailVal}
                onChange={(e) => {
                  setEmailVal(e.target.value);
                }}
              />
              <button
                onClick={addEmail}
                className={`text-gradient-pink text-3xl ${
                  !emailVal && 'cursor-not-allowed opacity-50 select-none'
                }`}
              >
                <i className="ri-add-circle-fill"></i>
              </button>
            </div>
            {errors.emailError.length ? (
              <p className="text-red-600 mt-2">{errors.emailError}</p>
            ) : null}
          </>
        )}
        {isEnabled.email && emails.length > 0 && (
          <div className="flex flex-wrap gap-2 my-2">
            {emails.map((item, index) => {
              return (
                <div
                  className="bg-primary p-2 rounded-md flex items-center gap-2"
                  key={item.concat(`${index}`)}
                >
                  <span>{item}</span>
                  <button onClick={() => removeEmail(index)}>
                    <i className="ri-close-large-line"></i>
                  </button>
                </div>
              );
            })}
          </div>
        )}
        {isEnabled.text && (
          <>
            <div className="flex items-center gap-2 mb-2">
              <PhoneInput
                onBlur={validateNumber}
                onCountryChange={(e) => {
                  if (e) {
                    setCountryCode(getCountryCallingCode(e));
                    setCountry(e);
                  }
                }}
                countryCallingCodeEditable={false}
                limitMaxLength
                placeholder="(XXX) XXX-XXXX"
                value={textVal}
                defaultCountry={country}
                className="input-border !rounded-lg !p-4 !w-[calc(100%-30px)]"
                onChange={(e) => {
                  setTextVal(e as any);
                }}
              />
              <button
                onClick={addPhone}
                className={`text-gradient-pink text-3xl ${
                  !textVal && 'cursor-not-allowed opacity-50 select-none'
                }`}
              >
                <i className="ri-add-circle-fill"></i>
              </button>
            </div>

            {errors.phoneError.length ? (
              <p className="text-red-600 mt-2">{errors.phoneError}</p>
            ) : null}
            {isEnabled.text && texts.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {texts.map((item, index) => {
                  return (
                    <div
                      className="bg-primary p-2 rounded-md flex items-center gap-2"
                      key={item.concat(`${index}`)}
                    >
                      <span>{item}</span>
                      <button onClick={() => removeNumber(index)}>
                        <i className="ri-close-large-line"></i>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ExternalUser;
